import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Badge,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  FormControlLabel,
  Checkbox,
  LinearProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  PersonAdd as LawyerShareIcon,
  Share as ShareIcon,
  UploadFile as UploadIcon,
  PictureAsPdf as PdfIcon,
  NotificationsActive as AlarmIcon,
  CheckCircleOutline as CheckIcon,
  Refresh as RefreshIcon,
  Draw as DrawIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { documentService } from '../services/documentService';
import { IDocumentSummary, IDocumentImport } from '../types/document';
import { IQuestionnaireExpiredAssignment } from '../types/questionnaire';
import { useAuthStore } from '../stores/authStore';
import DeleteDocumentButton from '../components/DeleteDocumentButton';

interface Lawyer {
  _id: string;
  name: string;
  email: string;
}

const DocumentLibrary: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, lastSeenAlarm, markAlarmsAsSeen } = useAuthStore();
  const isEO = user?.userType === 'EO';
  const isEL = user?.userType === 'EL';

  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState<'all' | 'questionnaire' | 'uploaded_pdf' | 'manual' | 'legacy_template'>('all');

  // Upload PDF dialog state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');

  // Share with Lawyer dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareTargetDoc, setShareTargetDoc] = useState<IDocumentSummary | null>(null);
  const [selectedLawyerId, setSelectedLawyerId] = useState('');
  const [extractionReviewed, setExtractionReviewed] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareError, setShareError] = useState('');
  const [shareSuccess, setShareSuccess] = useState('');

  // Share with Estate Executor dialog state
  const [shareExecutorDialogOpen, setShareExecutorDialogOpen] = useState(false);
  const [shareExecutorTargetDoc, setShareExecutorTargetDoc] = useState<IDocumentSummary | null>(null);
  const [selectedExecutorEmail, setSelectedExecutorEmail] = useState('');
  const [sharingExecutor, setSharingExecutor] = useState(false);
  const [shareExecutorError, setShareExecutorError] = useState('');
  const [shareExecutorSuccess, setShareExecutorSuccess] = useState('');

  // Alarm query for notification icon
  const { data: alarmAssignments = [] } = useQuery<IQuestionnaireExpiredAssignment[]>({
    queryKey: ['expiredAssignments', user?.id],
    queryFn: async () => {
      const response = await api.get<IQuestionnaireExpiredAssignment[]>('/documents/expired-assignments');
      return response.data;
    },
    enabled: !!user && user.role === 'PLUS',
  });

  const expiredCount = alarmAssignments.filter(a => new Date(a.expiresAt).getTime() > lastSeenAlarm).length;

  // Documents query
  const {
    data: documents = [],
    isLoading: loadingDocs,
    error: docsError,
    refetch: refetchDocs,
  } = useQuery<IDocumentSummary[]>({
    queryKey: ['documents', user?.id],
    queryFn: () => documentService.getDocuments(),
    enabled: !!user,
  });

  // Active imports query (polled if any are in progress)
  const { data: importJobs = [], refetch: refetchImports } = useQuery<IDocumentImport[]>({
    queryKey: ['documentImports', user?.id],
    queryFn: () => documentService.getDocumentImports(),
    enabled: !!user && isEO,
    refetchInterval: (query) => {
      const data = query.state.data;
      const hasActive = (data || []).some(j => j.status === 'queued' || j.status === 'extracting');
      return hasActive ? 3000 : false;
    },
  });

  // Associated lawyers query (for sharing)
  const { data: associatedLawyers = [] } = useQuery<Lawyer[]>({
    queryKey: ['associatedLawyers', user?.id],
    queryFn: async () => {
      const res = await api.get<Lawyer[]>('/lawyers');
      return res.data;
    },
    enabled: !!user && isEO,
  });

  // Registered executors query (for sharing)
  const { data: executors = [], isLoading: loadingExecutors } = useQuery<Array<{ _id: string; name: string; email: string }>>({
    queryKey: ['executors', user?.id],
    queryFn: () => documentService.getExecutors(),
    enabled: !!user && (isEO || isEL),
  });

  const activeImports = useMemo(
    () => importJobs.filter(j => j.status === 'queued' || j.status === 'extracting'),
    [importJobs]
  );

  const filteredDocuments = useMemo(() => {
    return documents.filter((doc) => {
      const matchesSearch =
        doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.userEmail.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesSource = sourceFilter === 'all' || doc.sourceType === sourceFilter;
      return matchesSearch && matchesSource;
    });
  }, [documents, searchTerm, sourceFilter]);

  // Handle file selection (PDF, DOCX, DOC)
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUploadError('');
    setUploadSuccess('');
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const lower = file.name.toLowerCase();
      if (!lower.endsWith('.pdf') && !lower.endsWith('.docx') && !lower.endsWith('.doc')) {
        setUploadError('Only PDF and Word documents (.pdf, .docx, .doc) are supported.');
        setSelectedFile(null);
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setUploadError('File size exceeds the 10 MB limit.');
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
    }
  };

  // Submit document upload
  const handleUploadSubmit = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setUploadError('');
    setUploadSuccess('');

    try {
      const result = await documentService.uploadDocumentImport({ file: selectedFile });
      if (result.status === 'failed') {
        setUploadError(result.warnings?.[0] || 'Document extraction failed. Please check the file and try again.');
        return;
      }
      setUploadSuccess('Document successfully uploaded and processed!');
      setSelectedFile(null);
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['documentImports'] });

      setTimeout(() => {
        setUploadDialogOpen(false);
        if (result.documentId) {
          navigate(`/document-library/documents/${result.documentId}`);
        }
      }, 1000);
    } catch (err: any) {
      setUploadError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to process PDF upload. Please verify file integrity and try again.'
      );
    } finally {
      setUploading(false);
    }
  };

  // Open Share with Lawyer Dialog
  const handleOpenShareDialog = (doc: IDocumentSummary) => {
    if (doc.review?.sharedWith.length || !doc.permissions?.canShare) return;
    setShareTargetDoc(doc);
    setSelectedLawyerId('');
    setExtractionReviewed(false);
    setShareError('');
    setShareSuccess('');
    setShareDialogOpen(true);
  };

  // Submit Share with Lawyer
  const handleShareSubmit = async () => {
    if (!shareTargetDoc || !selectedLawyerId) return;

    if (shareTargetDoc.sourceType === 'uploaded_pdf' && !extractionReviewed) {
      setShareError('You must review the extracted PDF content before sharing with a lawyer.');
      return;
    }

    setSharing(true);
    setShareError('');
    setShareSuccess('');

    try {
      await documentService.shareDocumentWithLawyer(shareTargetDoc.id, {
        lawyerId: selectedLawyerId,
        extractionReviewed: shareTargetDoc.sourceType === 'uploaded_pdf' ? true : undefined,
      });
      setShareSuccess('Document successfully shared with lawyer.');
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      setTimeout(() => {
        setShareDialogOpen(false);
      }, 1200);
    } catch (err: any) {
      setShareError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to share document with lawyer.'
      );
    } finally {
      setSharing(false);
    }
  };

  // Open Share with Estate Executor Dialog
  const handleOpenShareExecutorDialog = (doc: IDocumentSummary) => {
    setShareExecutorTargetDoc(doc);
    setSelectedExecutorEmail('');
    setShareExecutorError('');
    setShareExecutorSuccess('');
    setShareExecutorDialogOpen(true);
  };

  // Submit Share with Estate Executor
  const handleShareExecutorSubmit = async () => {
    if (!shareExecutorTargetDoc || !selectedExecutorEmail) return;

    setSharingExecutor(true);
    setShareExecutorError('');
    setShareExecutorSuccess('');

    try {
      await documentService.assignExecutor(shareExecutorTargetDoc.id, {
        executorEmail: selectedExecutorEmail,
        expiresInDays: 7,
      });
      setShareExecutorSuccess('Document successfully shared with executor.');
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      setTimeout(() => {
        setShareExecutorDialogOpen(false);
      }, 1200);
    } catch (err: any) {
      setShareExecutorError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to share document with executor.'
      );
    } finally {
      setSharingExecutor(false);
    }
  };

  // Revoke Lawyer Share
  const handleRevokeShare = async (docId: string, shareId: string) => {
    try {
      await documentService.revokeLawyerShare(docId, shareId);
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to revoke access.');
    }
  };

  const getSourceBadge = (sourceType: string) => {
    switch (sourceType) {
      case 'questionnaire':
        return <Chip label="Questionnaire" color="primary" size="small" variant="outlined" />;
      case 'uploaded_pdf':
        return <Chip label="Uploaded Document" color="secondary" size="small" variant="outlined" />;
      case 'manual':
        return <Chip label="Manual Creation" color="info" size="small" variant="outlined" />;
      case 'legacy_template':
      default:
        return <Chip label="Template" size="small" variant="outlined" />;
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Document History
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              Centralized record of your estate planning documents, imported PDFs, and revision history.
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              if (user?.role === 'PLUS') {
                markAlarmsAsSeen();
                navigate('/assigned-documents/alarms');
              }
            }}
            disabled={user?.role !== 'PLUS'}
          >
            <Badge badgeContent={expiredCount} color="error">
              <AlarmIcon color={user?.role === 'PLUS' ? 'action' : 'disabled'} />
            </Badge>
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {(isEO || isEL) && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<UploadIcon />}
              onClick={() => {
                setSelectedFile(null);
                setUploadError('');
                setUploadSuccess('');
                setUploadDialogOpen(true);
              }}
            >
              Upload Document
            </Button>
          )}
          <Button variant="outlined" onClick={() => navigate('/assigned-documents')}>
            Assigned Documents
          </Button>
        </Box>
      </Box>

      {/* Active Imports Notification Banner */}
      {activeImports.length > 0 && (
        <Alert severity="info" sx={{ mb: 3, alignItems: 'center' }}>
          <Box sx={{ width: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Extraction in progress
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Extracting text and structure verbatim from {activeImports.map(i => i.originalFilename).join(', ')}...
            </Typography>
            <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />
          </Box>
        </Alert>
      )}

      {docsError && (
        <Alert severity="error" sx={{ mb: 3 }} action={<Button size="small" onClick={() => refetchDocs()}>Retry</Button>}>
          Failed to load documents.
        </Alert>
      )}

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }} elevation={1}>
        <TextField
          size="small"
          placeholder="Search by title or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flexGrow: 1, minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Source</InputLabel>
          <Select
            value={sourceFilter}
            label="Source"
            onChange={(e) => setSourceFilter(e.target.value as any)}
          >
            <MenuItem value="all">All Sources</MenuItem>
            <MenuItem value="questionnaire">Questionnaire</MenuItem>
            <MenuItem value="uploaded_pdf">Uploaded PDF</MenuItem>
            <MenuItem value="legacy_template">Template</MenuItem>
          </Select>
        </FormControl>

        <IconButton onClick={() => { refetchDocs(); refetchImports(); }} title="Refresh list">
          <RefreshIcon />
        </IconButton>
      </Paper>

      {/* Documents Table */}
      <TableContainer component={Paper} elevation={1}>
        <Table sx={{ minWidth: 750 }}>
          <TableHead sx={{ backgroundColor: 'action.hover' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Document Title</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Revision</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              {isEL && <TableCell sx={{ fontWeight: 600 }}>Owner</TableCell>}
              <TableCell sx={{ fontWeight: 600 }}>Shared With</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Last Updated</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingDocs ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} />
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                    Loading documents...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredDocuments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No documents found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {searchTerm || sourceFilter !== 'all'
                      ? 'No documents match your current filter criteria.'
                      : 'You do not have any documents yet. Upload a document to get started.'}
                  </Typography>
                  {isEO && !searchTerm && sourceFilter === 'all' && (
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
                      <Button
                        variant="contained"
                        startIcon={<UploadIcon />}
                        onClick={() => setUploadDialogOpen(true)}
                      >
                        Upload Document
                      </Button>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              filteredDocuments.map((doc) => {
                const isFinalized = doc.finalized;
                const shares = doc.review?.sharedWith || [];
                const isLawyerQuestionnaire =
                  doc.sourceType === 'questionnaire' &&
                  Boolean(doc.source?.createdByLawyer || doc.source?.lawyerId);

                return (
                  <TableRow key={doc.id} hover>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {doc.title}
                      </Typography>
                    </TableCell>
                    <TableCell>{getSourceBadge(doc.sourceType)}</TableCell>
                    <TableCell>
                      <Chip label={`v${doc.revisionNumber}`} size="small" variant="filled" />
                    </TableCell>
                    <TableCell>
                      {isFinalized ? (
                        <Chip label="Finalized" color="success" size="small" icon={<CheckIcon />} />
                      ) : (
                        <Chip label="Draft" color="default" size="small" />
                      )}
                    </TableCell>
                    {isEL && (
                      <TableCell>
                        <Typography variant="body2">{doc.userEmail}</Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      {shares.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          Not shared
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {shares.map((s) => (
                            <Chip
                              key={s.id}
                              label={s.email}
                              size="small"
                              variant="outlined"
                              color="primary"
                              onDelete={
                                isEO && !isFinalized
                                  ? () => handleRevokeShare(doc.id, s.id)
                                  : undefined
                              }
                            />
                          ))}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(doc.updatedAt || doc.createdAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title="View Document">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/document-library/documents/${doc.id}`)}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Open Assigned Document & Sign">
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => navigate(`/assigned-documents/${doc.id}`)}
                            aria-label="Open Assigned Document"
                          >
                            <DrawIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {doc.permissions?.canEdit && !isFinalized && (
                          <Tooltip title="Edit Content">
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => navigate(`/document-library/documents/${doc.id}?edit=true`)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {isEO && (shares.length > 0 || (doc.permissions?.canShare && !isLawyerQuestionnaire && !isFinalized)) && (
                          <Tooltip title={shares.length > 0 ? 'Already shared with an Estate Lawyer' : 'Share with Estate Lawyer'}>
                            <span>
                              <IconButton
                                size="small"
                                color="info"
                                aria-label="Share with Estate Lawyer"
                                disabled={shares.length > 0 || !doc.permissions?.canShare}
                                onClick={() => handleOpenShareDialog(doc)}
                              >
                                <LawyerShareIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}

                        {(isEO || isEL) && !isFinalized && (
                          <Tooltip title="Share with Estate Executor">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenShareExecutorDialog(doc)}
                              aria-label="Share with Estate Executor"
                            >
                              <ShareIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {doc.permissions?.canDelete && (
                          <DeleteDocumentButton documentId={doc.id} title={doc.title} onDeleted={() => {
                            queryClient.invalidateQueries({ queryKey: ['documents'] });
                            queryClient.invalidateQueries({ queryKey: ['expiredAssignments'] });
                            queryClient.invalidateQueries({ queryKey: ['dashboardStats'] });
                          }} />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Upload Document Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => !uploading && setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Upload Document</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" paragraph>
            Upload an existing PDF or Word document (.pdf, .docx, .doc). Its full layout, wording, paragraphs, and tables will be extracted verbatim without rewriting or inventing missing clauses.
          </Typography>

          {uploadError && <Alert severity="error" sx={{ mb: 2 }}>{uploadError}</Alert>}
          {uploadSuccess && <Alert severity="success" sx={{ mb: 2 }}>{uploadSuccess}</Alert>}

          <input
            id="pdf-will-upload-input"
            type="file"
            accept=".pdf,.docx,.doc,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
            style={{ display: 'none' }}
            disabled={uploading}
            onChange={handleFileChange}
          />

          <Box
            component="label"
            htmlFor="pdf-will-upload-input"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px dashed',
              borderColor: selectedFile ? 'primary.main' : 'divider',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              backgroundColor: selectedFile ? 'action.selected' : 'action.hover',
              cursor: uploading ? 'default' : 'pointer',
              mb: 2,
              width: '100%',
              boxSizing: 'border-box',
              minHeight: 160,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: uploading ? 'divider' : 'primary.main',
                backgroundColor: selectedFile ? 'action.selected' : 'action.hover',
              },
            }}
          >
            {selectedFile ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  maxWidth: '100%',
                }}
              >
                <PdfIcon color="error" sx={{ fontSize: 48, mb: 1 }} />
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    maxWidth: '100%',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    textAlign: 'center',
                    mb: 0.5,
                  }}
                >
                  {selectedFile.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB &bull; Click to choose a different document
                </Typography>
              </Box>
            ) : (
              <>
                <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Click or drag document here to upload
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Maximum file size: 10 MB. PDF (.pdf) and Word (.docx, .doc) formats supported.
                </Typography>
              </>
            )}
          </Box>

          {uploading && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <CircularProgress size={28} sx={{ mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Extracting and formatting text verbatim... Please wait.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button disabled={uploading} onClick={() => setUploadDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!selectedFile || uploading}
            onClick={handleUploadSubmit}
          >
            {uploading ? 'Processing...' : 'Upload & Extract'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Document with Lawyer Dialog */}
      <Dialog
        open={shareDialogOpen}
        onClose={() => !sharing && setShareDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Share Document with Lawyer</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" paragraph>
            Grant an associated Estate Lawyer permission to review and edit <strong>{shareTargetDoc?.title}</strong>.
          </Typography>

          {shareError && <Alert severity="error" sx={{ mb: 2 }}>{shareError}</Alert>}
          {shareSuccess && <Alert severity="success" sx={{ mb: 2 }}>{shareSuccess}</Alert>}

          {associatedLawyers.length === 0 ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              You do not have any associated Estate Lawyers yet.
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setShareDialogOpen(false);
                    navigate('/lawyer-management');
                  }}
                >
                  Go to Lawyer Management
                </Button>
              </Box>
            </Alert>
          ) : (
            <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
              <InputLabel>Select Estate Lawyer</InputLabel>
              <Select
                value={selectedLawyerId}
                label="Select Estate Lawyer"
                onChange={(e) => setSelectedLawyerId(e.target.value)}
                disabled={sharing}
              >
                {associatedLawyers.map((lawyer) => (
                  <MenuItem key={lawyer._id} value={lawyer._id}>
                    {lawyer.name} ({lawyer.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {shareTargetDoc?.sourceType === 'uploaded_pdf' && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={extractionReviewed}
                    onChange={(e) => setExtractionReviewed(e.target.checked)}
                    color="primary"
                    disabled={sharing}
                  />
                }
                label={
                  <Typography variant="body2">
                    I have reviewed the extracted PDF document contents and verify its completeness before sharing with my lawyer.
                  </Typography>
                }
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button disabled={sharing} onClick={() => setShareDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              !selectedLawyerId ||
              sharing ||
              (shareTargetDoc?.sourceType === 'uploaded_pdf' && !extractionReviewed)
            }
            onClick={handleShareSubmit}
          >
            {sharing ? 'Sharing...' : 'Share Document'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Document with Estate Executor Dialog */}
      <Dialog
        open={shareExecutorDialogOpen}
        onClose={() => !sharingExecutor && setShareExecutorDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Share Document with Estate Executor</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" paragraph>
            Select an Estate Executor to share <strong>{shareExecutorTargetDoc?.title}</strong> with. They will receive an email invitation to view and sign the document.
          </Typography>

          {shareExecutorError && <Alert severity="error" sx={{ mb: 2 }}>{shareExecutorError}</Alert>}
          {shareExecutorSuccess && <Alert severity="success" sx={{ mb: 2 }}>{shareExecutorSuccess}</Alert>}

          {loadingExecutors ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : executors.length === 0 ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No registered Estate Executors found.
            </Alert>
          ) : (
            <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
              <InputLabel id="select-executor-label">Select Estate Executor</InputLabel>
              <Select
                labelId="select-executor-label"
                value={selectedExecutorEmail}
                label="Select Estate Executor"
                onChange={(e) => setSelectedExecutorEmail(e.target.value)}
                disabled={sharingExecutor}
              >
                {executors.map((executor) => (
                  <MenuItem key={executor._id} value={executor.email}>
                    {executor.name} ({executor.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button disabled={sharingExecutor} onClick={() => setShareExecutorDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!selectedExecutorEmail || sharingExecutor}
            onClick={handleShareExecutorSubmit}
          >
            {sharingExecutor ? 'Sharing...' : 'Share Document'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentLibrary;
